// src/pages/logistics/PageProductionPlan.jsx
import { useState, useEffect, useMemo } from "react";
import * as api from "../../apiService";
import { useToast } from "../../components/Toast";

function tomorrow() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

function fmt(d) {
  if (!d) return "—";
  const [y, m, day] = d.slice(0, 10).split("-");
  return `${day}/${m}/${y}`;
}

function dueSoon(dateStr) {
  if (!dateStr) return false;
  return (new Date(dateStr) - new Date()) / 86400000 <= 3;
}

function StatusBadge({ status }) {
  if (status === "FINAL")
    return <span style={{ background: "#dcfce7", color: "#166534", borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>✓ Finalized</span>;
  return <span style={{ background: "#fefce8", color: "#854d0e", borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>Draft</span>;
}

const FG_GROUPS = ["MFG Adhesives", "MFG Coolant", "MFG Lubricant Additives", "MFG Silicate Based Adhesives", "MFG Wax Emulsion"];

function FgStockSection() {
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const [cachedAt, setCachedAt] = useState(null);
  const [collapsed, setCollapsed] = useState(true);

  function load(refresh = false) {
    setLoading(true); setError(null);
    api.fetchFgStock(refresh)
      .then(data => { setItems(data); setCachedAt(new Date()); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }

  function handleToggle() {
    const next = !collapsed;
    setCollapsed(next);
    if (!next && items.length === 0 && !loading) load(false);
  }

  const byGroup = useMemo(() => {
    const map = {};
    FG_GROUPS.forEach(g => { map[g] = []; });
    items.forEach(item => { if (map[item.group]) map[item.group].push(item); });
    return map;
  }, [items]);

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div onClick={handleToggle} style={{
        padding: "9px 14px", borderBottom: collapsed ? "none" : "1px solid var(--color-border)",
        display: "flex", alignItems: "center", gap: 10, cursor: "pointer", userSelect: "none",
      }}>
        <span style={{ fontSize: 10, color: "#9ca3af" }}>{collapsed ? "▸" : "▾"}</span>
        <span style={{ fontWeight: 700, fontSize: 12 }}>FG Stock</span>
        {!loading && items.length > 0 && (
          <span style={{ fontSize: 11, color: "#6b7280" }}>
            {items.filter(i => i.qty > 0).length}/{items.length} grades in stock
          </span>
        )}
        {loading && <span style={{ fontSize: 11, color: "#9ca3af" }}>Fetching from Tally…</span>}
        {cachedAt && !loading && (
          <span style={{ fontSize: 10, color: "#9ca3af", marginLeft: "auto" }}>
            Synced {cachedAt.toLocaleTimeString("en-AE", { hour: "2-digit", minute: "2-digit" })}
          </span>
        )}
        {!collapsed && (
          <button className="btn" style={{ fontSize: 10, padding: "2px 8px", marginLeft: cachedAt ? 6 : "auto" }}
            onClick={e => { e.stopPropagation(); load(true); }}>↻ Refresh</button>
        )}
      </div>
      {!collapsed && (
        <div style={{ padding: "10px 14px" }}>
          {error && <div className="alert-warn" style={{ marginBottom: 8 }}>Error: {error}</div>}
          {!loading && items.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 10 }}>
              {FG_GROUPS.map(group => {
                const gi = byGroup[group] ?? [];
                if (gi.length === 0) return null;
                return (
                  <div key={group} style={{ border: "1px solid #e2e8f0", borderRadius: 5, overflow: "hidden" }}>
                    <div style={{ padding: "4px 10px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", fontSize: 10, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      {group.replace("MFG ", "")}
                    </div>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                      <tbody>
                        {gi.map(item => (
                          <tr key={item.name} style={{ borderBottom: "1px solid #f1f5f9" }}>
                            <td style={{ padding: "3px 8px", color: item.qty <= 0 ? "#9ca3af" : "#111827" }}>{item.name}</td>
                            <td style={{ padding: "3px 8px", textAlign: "right", fontWeight: item.qty > 0 ? 700 : 400, color: item.qty > 0 ? "#15803d" : "#9ca3af" }}>
                              {item.qty > 0 ? `${item.qty.toLocaleString()} ${item.unit}` : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Coverage badge — shows how much of a grade is already in the plan ──────────

function CoverageBadge({ grade, planLines, pendingQty }) {
  const covered = planLines
    .filter(l => l.grade?.toLowerCase() === grade?.toLowerCase())
    .reduce((s, l) => s + (parseFloat(l.plannedQty) || 0), 0);
  if (covered <= 0) return null;
  const full = pendingQty && covered >= Number(pendingQty);
  return (
    <span style={{
      fontSize: 10, padding: "1px 5px", borderRadius: 3, fontWeight: 600, whiteSpace: "nowrap",
      background: full ? "#dcfce7" : "#fefce8",
      color:      full ? "#15803d" : "#854d0e",
    }}>
      {full ? "✓" : "~"} {covered.toLocaleString()} KG in plan
    </span>
  );
}

// ── Open Orders (left column) ─────────────────────────────────────────────────

function flattenOrders(data, bu) {
  const rows = [];
  for (const group of (data?.groups ?? [])) {
    for (const order of group.orders) {
      for (const line of order.lines) {
        if (line.isNegative) continue;
        rows.push({ item: line.item, pendingQty: line.pendingQty, dueDate: order.dueDate, orderRef: order.ref, bu });
      }
    }
  }
  rows.sort((a, b) => (a.dueDate || "9999") < (b.dueDate || "9999") ? -1 : 1);
  return rows;
}

function OrderTable({ rows, onAddToPlan, planLines, inactive }) {
  if (rows.length === 0)
    return <div style={{ padding: "10px 14px", color: "#9ca3af", fontSize: 11 }}>No open orders.</div>;
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, opacity: inactive ? 0.5 : 1 }}>
      <thead>
        <tr style={{ borderBottom: "1px solid #e2e8f0", background: inactive ? "#f3f4f6" : "#f8fafc" }}>
          <th style={{ padding: "5px 10px", textAlign: "left", fontWeight: 600 }}>Grade</th>
          <th style={{ padding: "5px 10px", textAlign: "right", fontWeight: 600 }}>Qty</th>
          <th style={{ padding: "5px 10px", textAlign: "left", fontWeight: 600 }}>Due</th>
          {!inactive && <th style={{ padding: "5px 6px", width: 36 }}></th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} style={{ borderBottom: "1px solid #f1f5f9", background: (!inactive && dueSoon(r.dueDate)) ? "#fff7ed" : "white" }}>
            <td style={{ padding: "5px 10px" }}>
              <div style={{ fontWeight: 600, fontFamily: "monospace", color: inactive ? "#9ca3af" : "#111827", fontSize: 11 }}>{r.item}</div>
              {!inactive && (
                <div style={{ marginTop: 2 }}>
                  <CoverageBadge grade={r.item} planLines={planLines} pendingQty={r.pendingQty} />
                </div>
              )}
            </td>
            <td style={{ padding: "5px 10px", textAlign: "right", fontWeight: 600, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
              {r.pendingQty}
            </td>
            <td style={{ padding: "5px 10px", whiteSpace: "nowrap",
              color: (!inactive && dueSoon(r.dueDate)) ? "#c2410c" : "#374151",
              fontWeight: (!inactive && dueSoon(r.dueDate)) ? 700 : 400 }}>
              {fmt(r.dueDate)}
              {!inactive && dueSoon(r.dueDate) && <span style={{ display: "block", fontSize: 9, color: "#c2410c" }}>SOON</span>}
            </td>
            {!inactive && (
              <td style={{ padding: "5px 6px" }}>
                <button
                  className="btn-small btn-primary"
                  style={{ fontSize: 10, padding: "2px 8px" }}
                  onClick={() => onAddToPlan({ grade: r.item, orderRef: r.orderRef, source: "ORDER", plannedQty: String(r.pendingQty || "") })}>
                  +
                </button>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CompanyHeader({ label, count, inactive }) {
  return (
    <div style={{
      padding: "5px 14px", fontSize: 10, fontWeight: 700, letterSpacing: "0.05em",
      textTransform: "uppercase", display: "flex", alignItems: "center", gap: 8,
      background: inactive ? "#f3f4f6" : "#eef2ff",
      color: inactive ? "#9ca3af" : "#4338ca",
      borderTop: "1px solid var(--color-border)",
    }}>
      {label}
      <span style={{ fontWeight: 400, fontSize: 10 }}>{count} line{count !== 1 ? "s" : ""}</span>
      {inactive && (
        <span style={{ marginLeft: "auto", fontSize: 9, background: "#e5e7eb", color: "#6b7280", borderRadius: 3, padding: "1px 6px" }}>
          Trading — no production
        </span>
      )}
    </div>
  );
}

function OpenOrdersSection({ onAddToPlan, planLines }) {
  const [amiRows, setAmiRows] = useState([]);
  const [amtRows, setAmtRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [search,  setSearch]  = useState("");

  useEffect(() => {
    setLoading(true);
    Promise.all([api.fetchOrderBook("AMI"), api.fetchOrderBook("AMT")])
      .then(([ami, amt]) => {
        setAmiRows(flattenOrders(ami, "AMI"));
        setAmtRows(flattenOrders(amt, "AMT"));
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const [visibleAmi, visibleAmt] = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [amiRows, amtRows];
    const f = rows => rows.filter(r => r.item.toLowerCase().includes(q));
    return [f(amiRows), f(amtRows)];
  }, [amiRows, amtRows, search]);

  const totalLines = amiRows.length + amtRows.length;

  return (
    <div className="card" style={{ position: "sticky", top: 12 }}>
      <div style={{
        padding: "9px 14px", borderBottom: "1px solid var(--color-border)",
        display: "flex", alignItems: "center", gap: 8,
      }}>
        <span style={{ fontWeight: 700, fontSize: 12 }}>Open Orders</span>
        <span style={{ fontSize: 11, color: "#6b7280" }}>{totalLines} lines</span>
        <input
          className="form-input"
          placeholder="Search…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ marginLeft: "auto", width: 120, fontSize: 11, padding: "3px 7px" }}
        />
      </div>

      {loading && <div style={{ padding: 12, color: "#9ca3af", fontSize: 11 }}>Loading from Tally…</div>}
      {error   && <div className="alert-warn" style={{ margin: 10, fontSize: 11 }}>Error: {error}</div>}

      {!loading && !error && (
        <div style={{ overflowY: "auto", maxHeight: "calc(100vh - 200px)" }}>
          <CompanyHeader label="AMI — Industries" count={visibleAmi.length} inactive={false} />
          <OrderTable rows={visibleAmi} onAddToPlan={onAddToPlan} planLines={planLines} inactive={false} />
          <CompanyHeader label="AMT — Trading" count={visibleAmt.length} inactive={true} />
          <OrderTable rows={visibleAmt} onAddToPlan={null} planLines={planLines} inactive={true} />
        </div>
      )}
    </div>
  );
}

// ── Plan Section (right column) ───────────────────────────────────────────────

const EMPTY_LINE = { grade: "", batchRef: "", plannedQty: "", packing: "", source: "ORDER", orderRef: "", notes: "" };

const inSx = {
  fontSize: 11, padding: "3px 6px", border: "1px solid #d1d5db",
  borderRadius: 3, background: "white", width: "100%",
};

function fmtMinutes(m) {
  if (!m) return "—";
  const h = Math.floor(m / 60), r = m % 60;
  return r > 0 ? `${h}h ${r}m` : `${h}h`;
}

function SuggestionPanel({ result, onAccept, onDismiss }) {
  const [selected, setSelected] = useState(() => {
    const s = {};
    result.suggestions.forEach((sg, i) => { if (sg.source !== "STOCK_COVERS" && sg.netDemand > 0) s[i] = true; });
    return s;
  });

  function toggle(i) { setSelected(p => ({ ...p, [i]: !p[i] })); }
  function toggleAll(val) {
    const s = {};
    result.suggestions.forEach((sg, i) => { if (sg.source !== "STOCK_COVERS" && sg.netDemand > 0) s[i] = val; });
    setSelected(s);
  }

  function handleAccept() {
    const lines = result.suggestions
      .filter((_, i) => selected[i])
      .map(sg => ({
        grade:      sg.grade,
        plannedQty: String(sg.suggestedQty),
        source:     "ORDER",
        notes:      `${sg.mixerName} · ${sg.batchCount} batch${sg.batchCount !== 1 ? "es" : ""}`,
        batchRef:   "", packing: "", orderRef: "",
      }));
    onAccept(lines);
  }

  const acceptCount = Object.values(selected).filter(Boolean).length;

  return (
    <div style={{ border: "1px solid #bfdbfe", borderRadius: 6, background: "#eff6ff", marginBottom: 12, overflow: "hidden" }}>
      {/* Summary bar */}
      <div style={{ padding: "8px 14px", background: "#1d4ed8", color: "white", display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ fontWeight: 700, fontSize: 12 }}>Plan Suggestions — {result.dayOfWeek} · {result.date}</span>
        <span style={{ fontSize: 11, opacity: 0.85 }}>
          {fmtMinutes(result.workingMinutes)} available · ~{fmtMinutes(result.totalTimeMinutes)} total work
        </span>
        {result.totalTimeMinutes > result.workingMinutes && (
          <span style={{ fontSize: 11, background: "#fef2f2", color: "#b91c1c", borderRadius: 3, padding: "1px 6px", fontWeight: 600 }}>
            ⚠ Exceeds day capacity
          </span>
        )}
        <button onClick={onDismiss} style={{ marginLeft: "auto", background: "none", border: "none", color: "white", cursor: "pointer", fontSize: 14 }}>✕</button>
      </div>

      {/* Suggestions table */}
      {result.suggestions.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ background: "#dbeafe", borderBottom: "1px solid #bfdbfe" }}>
                <th style={{ padding: "5px 8px", width: 28 }}>
                  <input type="checkbox" onChange={e => toggleAll(e.target.checked)} />
                </th>
                <th style={{ padding: "5px 8px", textAlign: "left" }}>Grade</th>
                <th style={{ padding: "5px 8px", textAlign: "right" }}>Order KG</th>
                <th style={{ padding: "5px 8px", textAlign: "right" }}>Stock KG</th>
                <th style={{ padding: "5px 8px", textAlign: "right" }}>Net Need</th>
                <th style={{ padding: "5px 8px", textAlign: "left" }}>Mixer</th>
                <th style={{ padding: "5px 8px", textAlign: "center" }}>Batches</th>
                <th style={{ padding: "5px 8px", textAlign: "right" }}>Planned KG</th>
                <th style={{ padding: "5px 8px", textAlign: "right" }}>Time</th>
                <th style={{ padding: "5px 8px", textAlign: "center" }}>Due</th>
                <th style={{ padding: "5px 8px", width: 50 }}></th>
              </tr>
            </thead>
            <tbody>
              {result.suggestions.map((sg, i) => {
                const covered = sg.source === "STOCK_COVERS";
                const overDay = !sg.fitsInDay && !covered;
                return (
                  <tr key={i} style={{
                    borderBottom: "1px solid #bfdbfe",
                    background: covered ? "#f0fdf4" : overDay ? "#fff7ed" : "white",
                    opacity: covered ? 0.7 : 1,
                  }}>
                    <td style={{ padding: "4px 8px", textAlign: "center" }}>
                      {!covered && sg.netDemand > 0 && (
                        <input type="checkbox" checked={!!selected[i]} onChange={() => toggle(i)} />
                      )}
                    </td>
                    <td style={{ padding: "4px 8px", fontWeight: 600, fontFamily: "monospace" }}>{sg.grade}</td>
                    <td style={{ padding: "4px 8px", textAlign: "right" }}>{sg.totalOrderQty?.toLocaleString()}</td>
                    <td style={{ padding: "4px 8px", textAlign: "right", color: sg.stockQty > 0 ? "#15803d" : "#9ca3af" }}>
                      {sg.stockQty > 0 ? sg.stockQty?.toLocaleString() : "—"}
                    </td>
                    <td style={{ padding: "4px 8px", textAlign: "right", fontWeight: covered ? 400 : 700, color: covered ? "#15803d" : "#111827" }}>
                      {covered ? "Covered ✓" : sg.netDemand?.toLocaleString()}
                    </td>
                    <td style={{ padding: "4px 8px", color: "#374151" }}>{sg.mixerName}</td>
                    <td style={{ padding: "4px 8px", textAlign: "center", fontWeight: 600 }}>{sg.batchCount || "—"}</td>
                    <td style={{ padding: "4px 8px", textAlign: "right", fontWeight: 600 }}>
                      {sg.suggestedQty > 0 ? sg.suggestedQty?.toLocaleString() : "—"}
                    </td>
                    <td style={{ padding: "4px 8px", textAlign: "right", color: overDay ? "#c2410c" : "#374151" }}>
                      {sg.totalTimeMinutes > 0 ? fmtMinutes(sg.totalTimeMinutes) : "—"}
                      {overDay && <span style={{ display: "block", fontSize: 9, color: "#c2410c" }}>OVERFLOW</span>}
                    </td>
                    <td style={{ padding: "4px 8px", textAlign: "center", color: dueSoon(sg.dueDate) ? "#c2410c" : "#6b7280", fontWeight: dueSoon(sg.dueDate) ? 700 : 400 }}>
                      {fmt(sg.dueDate)}
                    </td>
                    <td style={{ padding: "4px 8px" }}></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Unmatched */}
      {result.unmatched?.length > 0 && (
        <div style={{ padding: "8px 14px", borderTop: "1px solid #bfdbfe", background: "#fef9c3" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#854d0e", textTransform: "uppercase", marginBottom: 4 }}>
            No mixer assigned — configure in Mixer Config
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {result.unmatched.map((u, i) => (
              <span key={i} style={{ fontSize: 11, background: "white", border: "1px solid #fde68a", borderRadius: 3, padding: "2px 8px", fontFamily: "monospace" }}>
                {u.grade} · {u.netDemand?.toLocaleString()} KG
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Accept bar */}
      <div style={{ padding: "8px 14px", borderTop: "1px solid #bfdbfe", display: "flex", alignItems: "center", gap: 8, background: "#f8faff" }}>
        <button className="btn btn-primary" style={{ fontSize: 12 }}
          onClick={handleAccept} disabled={acceptCount === 0}>
          Add {acceptCount} Selected to Plan
        </button>
        <button className="btn" style={{ fontSize: 11 }} onClick={onDismiss}>Dismiss</button>
        <span style={{ fontSize: 11, color: "#6b7280", marginLeft: "auto" }}>
          {result.suggestions.filter(s => s.source !== "STOCK_COVERS").length} grades to produce · {result.unmatched?.length ?? 0} unassigned
        </span>
      </div>
    </div>
  );
}

function PlanSection({ addLineRef, myPerms, planDate, onPlanDateChange, onLinesChange, planStatus, onStatusChange }) {
  const showToast = useToast();
  const canEdit       = myPerms?.includes("RM_EDIT")      && planStatus !== "FINAL";
  const canFinalize   = myPerms?.includes("USER_MANAGE");

  const [lines,      setLines]      = useState([]);
  const [saving,     setSaving]     = useState(false);
  const [saved,      setSaved]      = useState(false);
  const [loading,    setLoading]    = useState(false);
  const [working,    setWorking]    = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState(null);

  // Expose addLine callback to parent
  addLineRef.current = (lineData) => {
    setLines(prev => {
      const exists = prev.some(l => l.orderRef === lineData.orderRef && l.grade === lineData.grade && lineData.orderRef);
      if (exists) return prev;
      return [...prev, { ...EMPTY_LINE, ...lineData }];
    });
  };

  useEffect(() => {
    setLoading(true);
    setSaved(false);
    api.fetchProductionPlan(planDate)
      .then(plan => {
        if (plan?.lines) {
          const loaded = plan.lines.map(l => ({
            grade:      l.grade       ?? "",
            batchRef:   l.batch_ref   ?? "",
            plannedQty: l.planned_qty != null ? String(l.planned_qty) : "",
            packing:    l.packing     ?? "",
            source:     l.source      ?? "ORDER",
            orderRef:   l.order_ref   ?? "",
            notes:      l.notes       ?? "",
          }));
          setLines(loaded);
          onStatusChange?.(plan.status ?? "DRAFT");
          onLinesChange?.(loaded);
        } else {
          setLines([]);
          onStatusChange?.("DRAFT");
          onLinesChange?.([]);
        }
      })
      .catch(() => { setLines([]); onStatusChange?.("DRAFT"); onLinesChange?.([]); })
      .finally(() => setLoading(false));
  }, [planDate]);

  // Lift lines to parent for coverage indicator on user edits
  useEffect(() => {
    onLinesChange?.(lines);
  }, [lines]); // onLinesChange is stable (setPlanLines from useState)

  function updateLine(i, field, value) {
    setLines(prev => prev.map((l, idx) => idx === i ? { ...l, [field]: value } : l));
  }

  function removeLine(i) {
    setLines(prev => prev.filter((_, idx) => idx !== i));
  }

  function addManualLine(source) {
    setLines(prev => [...prev, { ...EMPTY_LINE, source }]);
  }

  async function handleSave() {
    setSaving(true);
    try {
      await api.saveProductionPlan({ planDate, lines: lines.filter(l => l.grade.trim()) });
      setSaved(true);
      onStatusChange?.("DRAFT");
      showToast("Production plan saved");
      setTimeout(() => setSaved(false), 3000);
    } finally {
      setSaving(false);
    }
  }

  async function handleSetStatus(status) {
    setWorking(true);
    try {
      await api.setProductionPlanStatus(planDate, status);
      onStatusChange?.(status);
      showToast(status === "FINAL" ? "Plan finalized" : "Plan reverted to draft");
    } catch (e) {
      alert("Failed: " + e.message);
    } finally {
      setWorking(false);
    }
  }

  async function handleSuggest() {
    setSuggesting(true); setSuggestion(null);
    try {
      const result = await api.suggestProductionPlan(planDate);
      setSuggestion(result);
    } catch (e) {
      alert("Suggest failed: " + e.message);
    } finally {
      setSuggesting(false);
    }
  }

  function handleAcceptSuggestions(newLines) {
    setLines(prev => {
      const merged = [...prev];
      for (const l of newLines) {
        const exists = merged.some(p => p.grade === l.grade && p.source === l.source);
        if (!exists) merged.push(l);
      }
      return merged;
    });
    setSuggestion(null);
  }

  const orderLines = lines.filter(l => l.source === "ORDER");
  const stockLines = lines.filter(l => l.source === "STOCK");

  function renderRows(subset, sourceFilter) {
    const indices = lines.reduce((acc, l, i) => { if (l.source === sourceFilter) acc.push(i); return acc; }, []);
    if (subset.length === 0) return (
      <tr><td colSpan={canEdit ? 6 : 5} style={{ textAlign: "center", color: "#9ca3af", padding: 10, fontSize: 11 }}>None added.</td></tr>
    );
    return subset.map((line, si) => {
      const i = indices[si];
      return (
        <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
          <td style={{ textAlign: "center", color: "#9ca3af", fontSize: 10, width: 24, padding: "4px 6px" }}>{si + 1}</td>
          <td style={{ padding: "4px 6px" }}>
            {canEdit
              ? <input style={inSx} value={line.grade} onChange={e => updateLine(i, "grade", e.target.value)} placeholder="Grade" />
              : <span style={{ fontFamily: "monospace", fontWeight: 600, fontSize: 11 }}>{line.grade || "—"}</span>
            }
            {line.orderRef && <div style={{ fontSize: 9, color: "#9ca3af" }}>{line.orderRef}</div>}
          </td>
          <td style={{ padding: "4px 6px" }}>
            {canEdit
              ? <input style={inSx} value={line.batchRef} onChange={e => updateLine(i, "batchRef", e.target.value)} placeholder="Batch ref" />
              : <span style={{ fontSize: 11 }}>{line.batchRef || "—"}</span>
            }
          </td>
          <td style={{ padding: "4px 6px", width: 90 }}>
            {canEdit
              ? <input style={{ ...inSx, textAlign: "right" }} type="number" min="0" value={line.plannedQty} onChange={e => updateLine(i, "plannedQty", e.target.value)} placeholder="0" />
              : <span style={{ fontVariantNumeric: "tabular-nums", fontSize: 11 }}>{line.plannedQty ? `${line.plannedQty} KG` : "—"}</span>
            }
          </td>
          <td style={{ padding: "4px 6px" }}>
            {canEdit
              ? <input style={inSx} value={line.packing} onChange={e => updateLine(i, "packing", e.target.value)} placeholder="200KG × 10 DRUMS" />
              : <span style={{ fontSize: 11 }}>{line.packing || "—"}</span>
            }
          </td>
          {canEdit && (
            <td style={{ padding: "4px 6px", width: 24 }}>
              <button onClick={() => removeLine(i)} style={{ background: "none", border: "none", color: "#dc2626", cursor: "pointer", fontSize: 13, lineHeight: 1 }}>✕</button>
            </td>
          )}
        </tr>
      );
    });
  }

  function PlanSubSection({ label, subset, source, divider }) {
    return (
      <>
        <div style={{
          padding: "7px 14px 4px", fontSize: 10, fontWeight: 700, color: "#6b7280",
          letterSpacing: "0.06em", textTransform: "uppercase",
          borderTop: divider ? "2px solid #f1f5f9" : "none", marginTop: divider ? 6 : 0,
        }}>
          {label}
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #e2e8f0", background: "#f8fafc" }}>
                <th style={{ padding: "4px 6px", width: 24 }}></th>
                <th style={{ padding: "4px 6px", textAlign: "left" }}>Grade</th>
                <th style={{ padding: "4px 6px", textAlign: "left" }}>Batch Ref</th>
                <th style={{ padding: "4px 6px", textAlign: "right", width: 90 }}>Qty (KG)</th>
                <th style={{ padding: "4px 6px", textAlign: "left" }}>Packing</th>
                {canEdit && <th style={{ width: 24 }}></th>}
              </tr>
            </thead>
            <tbody>{renderRows(subset, source)}</tbody>
          </table>
        </div>
        {canEdit && (
          <div style={{ padding: "5px 14px" }}>
            <button className="btn" style={{ fontSize: 11, padding: "3px 10px" }} onClick={() => addManualLine(source)}>
              + Add {source === "ORDER" ? "Order" : "Stock Build"} Item
            </button>
          </div>
        )}
      </>
    );
  }

  const filledCount = lines.filter(l => l.grade.trim()).length;

  return (
    <div className="card">
      <div style={{
        padding: "9px 14px", borderBottom: "1px solid var(--color-border)",
        display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
      }}>
        <span style={{ fontWeight: 700, fontSize: 12 }}>Production Plan</span>
        <input type="date" value={planDate} onChange={e => onPlanDateChange(e.target.value)}
          className="form-input" style={{ width: 140, fontSize: 11 }} />
        {loading && <span style={{ fontSize: 11, color: "#9ca3af" }}>Loading…</span>}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          <button className="btn" style={{ fontSize: 11, padding: "3px 12px", background: "#f0fdf4", color: "#166534", border: "1px solid #bbf7d0" }}
            onClick={handleSuggest} disabled={suggesting || planStatus === "FINAL"}>
            {suggesting ? "Analysing…" : "✦ Suggest Plan"}
          </button>
          <StatusBadge status={planStatus} />
        </div>
      </div>

      {suggestion && (
        <div style={{ padding: "10px 14px" }}>
          <SuggestionPanel
            result={suggestion}
            onAccept={handleAcceptSuggestions}
            onDismiss={() => setSuggestion(null)}
          />
        </div>
      )}

      <PlanSubSection label="Orders"      subset={orderLines} source="ORDER" divider={false} />
      <PlanSubSection label="Stock Build" subset={stockLines} source="STOCK" divider={true}  />

      <div style={{
        padding: "9px 14px", borderTop: "1px solid var(--color-border)",
        display: "flex", alignItems: "center", gap: 8, background: "#fafafa", flexWrap: "wrap",
      }}>
        {canEdit && (
          <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ fontSize: 12 }}>
            {saving ? "Saving…" : "Save Draft"}
          </button>
        )}
        {saved && <span style={{ fontSize: 11, color: "#15803d" }}>✓ Saved</span>}
        {canFinalize && planStatus === "DRAFT" && filledCount > 0 && (
          <button className="btn" disabled={working}
            style={{ fontSize: 11, background: "#dcfce7", color: "#166534", border: "1px solid #bbf7d0" }}
            onClick={() => handleSetStatus("FINAL")}>
            {working ? "…" : "✓ Finalize Plan"}
          </button>
        )}
        {canFinalize && planStatus === "FINAL" && (
          <button className="btn" disabled={working} style={{ fontSize: 11 }}
            onClick={() => handleSetStatus("DRAFT")}>
            {working ? "…" : "Revert to Draft"}
          </button>
        )}
        <span style={{ marginLeft: "auto", fontSize: 10, color: "#9ca3af" }}>
          {orderLines.length} order{orderLines.length !== 1 ? "s" : ""} · {stockLines.length} stock build{stockLines.length !== 1 ? "s" : ""}
        </span>
      </div>
    </div>
  );
}

// ── Mixer Reference Card ──────────────────────────────────────────────────────

function MixerSection() {
  const [mixers,    setMixers]    = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);
  const [collapsed, setCollapsed] = useState(true);

  function load() {
    setLoading(true); setError(null);
    api.fetchMixers()
      .then(setMixers)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }

  function handleToggle() {
    const next = !collapsed;
    setCollapsed(next);
    if (!next && mixers.length === 0 && !loading) load();
  }

  const active = mixers.filter(m => m.active);

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div onClick={handleToggle} style={{
        padding: "9px 14px", borderBottom: collapsed ? "none" : "1px solid var(--color-border)",
        display: "flex", alignItems: "center", gap: 10, cursor: "pointer", userSelect: "none",
      }}>
        <span style={{ fontSize: 10, color: "#9ca3af" }}>{collapsed ? "▸" : "▾"}</span>
        <span style={{ fontWeight: 700, fontSize: 12 }}>Mixer Capacities</span>
        {!loading && mixers.length > 0 && (
          <span style={{ fontSize: 11, color: "#6b7280" }}>
            {active.length} active · {mixers.length - active.length} inactive
          </span>
        )}
        {loading && <span style={{ fontSize: 11, color: "#9ca3af" }}>Loading…</span>}
        {!collapsed && (
          <button className="btn" style={{ fontSize: 10, padding: "2px 8px", marginLeft: "auto" }}
            onClick={e => { e.stopPropagation(); load(); }}>↻ Refresh</button>
        )}
      </div>

      {!collapsed && !loading && !error && mixers.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #e2e8f0", background: "#f8fafc" }}>
                <th style={{ padding: "5px 10px", textAlign: "left", fontWeight: 600 }}>Mixer</th>
                <th style={{ padding: "5px 10px", textAlign: "left", fontWeight: 600 }}>Blade</th>
                <th style={{ padding: "5px 10px", textAlign: "center", fontWeight: 600 }}>Heat</th>
                <th style={{ padding: "5px 10px", textAlign: "right", fontWeight: 600 }}>Capacity (KG)</th>
                <th style={{ padding: "5px 10px", textAlign: "right", fontWeight: 600 }}>Batch Time</th>
                <th style={{ padding: "5px 10px", textAlign: "right", fontWeight: 600 }}>Batches/Day</th>
                <th style={{ padding: "5px 10px", textAlign: "left", fontWeight: 600 }}>Grades</th>
              </tr>
            </thead>
            <tbody>
              {mixers.map(m => {
                const bpd = m.batch_time_minutes ? Math.floor(540 / m.batch_time_minutes) : null;
                return (
                  <tr key={m.id} style={{
                    borderBottom: "1px solid #f1f5f9",
                    background: m.active ? "white" : "#f9fafb",
                    opacity: m.active ? 1 : 0.55,
                  }}>
                    <td style={{ padding: "5px 10px", fontWeight: 700, fontFamily: "monospace", color: m.active ? "#1d4ed8" : "#9ca3af" }}>
                      {m.name}
                    </td>
                    <td style={{ padding: "5px 10px", color: "#374151" }}>{m.blade_type}</td>
                    <td style={{ padding: "5px 10px", textAlign: "center" }}>
                      {m.heating
                        ? <span style={{ color: "#dc2626", fontWeight: 700 }}>✓</span>
                        : <span style={{ color: "#d1d5db" }}>—</span>}
                    </td>
                    <td style={{ padding: "5px 10px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                      {m.capacity_min_kg === m.capacity_max_kg
                        ? m.capacity_max_kg.toLocaleString()
                        : `${m.capacity_min_kg.toLocaleString()} – ${m.capacity_max_kg.toLocaleString()}`}
                    </td>
                    <td style={{ padding: "5px 10px", textAlign: "right", color: "#374151" }}>
                      {m.batch_time_minutes ? fmtMinutes(m.batch_time_minutes) : <span style={{ color: "#9ca3af" }}>—</span>}
                    </td>
                    <td style={{ padding: "5px 10px", textAlign: "right", fontWeight: bpd ? 600 : 400, color: bpd ? "#111827" : "#9ca3af" }}>
                      {bpd ?? "—"}
                    </td>
                    <td style={{ padding: "5px 10px" }}>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                        {(m.grades ?? []).map(g => (
                          <span key={g} style={{
                            fontSize: 10, fontFamily: "monospace", padding: "1px 5px",
                            background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 3,
                          }}>{g}</span>
                        ))}
                        {(m.grades ?? []).length === 0 && <span style={{ color: "#9ca3af", fontSize: 10 }}>No grades</span>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!collapsed && error && (
        <div className="alert-warn" style={{ margin: 10, fontSize: 11 }}>Error: {error}</div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function PageProductionPlan({ myPerms }) {
  const [planDate,   setPlanDate]   = useState(tomorrow());
  const [planLines,  setPlanLines]  = useState([]);
  const [planStatus, setPlanStatus] = useState("DRAFT");
  const addLineRef = { current: null };

  function handleAddToPlan(lineData) {
    addLineRef.current?.(lineData);
  }

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Production Planning</div>
          <div className="page-subtitle">Orders and stock builds — sorted by due date</div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "340px 1fr", gap: 16, alignItems: "start" }}>
        <OpenOrdersSection onAddToPlan={handleAddToPlan} planLines={planLines} />
        <div>
          <FgStockSection />
          <MixerSection />
          <PlanSection
            addLineRef={addLineRef}
            myPerms={myPerms}
            planDate={planDate}
            onPlanDateChange={setPlanDate}
            onLinesChange={setPlanLines}
            planStatus={planStatus}
            onStatusChange={setPlanStatus}
          />
        </div>
      </div>
    </>
  );
}
